import { CircleInvitation, CustomAxiosError } from '@calm/evexia-web-sdk';
import createInvitationsApi from '@rhg/libs/api/create-invitations-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import {
    identifyInvitationErrorType,
    InvitationErrorType,
} from '@rhg/utils/invitations';

interface CheckInvitationParameters {
    inviteCode: string;
}

interface InvitationByCodeResponse {
    invitation: CircleInvitation | null;
    invitationError: InvitationErrorType | null;
}

export const invitationByCodeQueryOptions = {
    queryKey: function (inviteCode?: string) {
        return [queryKeys.invitationByCode, inviteCode];
    },
    queryFn: async function (
        getAccessTokenSilently: () => Promise<string>,
        inviteCode: string
    ): Promise<InvitationByCodeResponse> {
        const invitationsApi = createInvitationsApi(getAccessTokenSilently);

        return invitationsApi
            .getInvitationByCodeV1({
                inviteCode,
            })
            .then((response) => ({
                invitation: response?.data ?? null,
                invitationError: null,
            }))
            .catch((e: CustomAxiosError) => {
                const invitationError = identifyInvitationErrorType(e);
                if (invitationError) {
                    return { invitation: null, invitationError };
                } else if (
                    e?.response?.status === 404 ||
                    e?.response?.status === 410
                ) {
                    return { invitation: null, invitationError: 'expired' };
                } else {
                    throw e;
                }
            });
    },
    queryOptions: function (
        isAuthenticated: boolean,
        inviteCode?: string,
        isEnabled?: boolean
    ) {
        return {
            enabled: !!(isAuthenticated && inviteCode && isEnabled),
            retry: false,
            staleTime: 1000 * 60 * 15, // 15 minutes
        };
    },
};

export function useInvitationByCode(inviteCode?: string, isEnabled = true) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn, queryOptions } = invitationByCodeQueryOptions;

    const options = queryOptions(isAuthenticated, inviteCode, isEnabled);

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(inviteCode),
        queryFn: async () => queryFn(getAccessTokenSilently, inviteCode!),
        ...options,
    });
    return {
        invitation: data?.invitation,
        invitationError: data?.invitationError,
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useCheckInvitation() {
    const { getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn } = invitationByCodeQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({ inviteCode }: CheckInvitationParameters) => {
            return queryFn(getAccessTokenSilently, inviteCode!);
        },
        onSuccess: (response, { inviteCode }) => {
            if (response.invitation) {
                queryClient.setQueryData(queryKey(inviteCode), response);
            }
        },
    });
    return {
        checkInvitation: mutate,
        checkInvitationAsync: mutateAsync,
        ...otherProps,
    };
}
